import { type DialogProps } from "@radix-ui/react-dialog"
import {
  CircleIcon,
  LaptopIcon,
  MoonIcon,
  SunIcon,
} from "@radix-ui/react-icons"
import { Search } from "lucide-react"
import * as React from "react"
import { useNavigate } from "react-router-dom"

import { useAllowedNavigation } from "@/lib/module-access"
import { cn } from "@/utils/classes"

import { useTheme } from "../theme"
import { Button } from "../ui/button"
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "../ui/control"

export function CommandMenu({
  shouldShowBuildingSwitcher,
  ...props
}: DialogProps & { shouldShowBuildingSwitcher: boolean }) {
  const navigate = useNavigate()
  const { setTheme } = useTheme()
  const [open, setOpen] = React.useState(false)
  const { items: navigation } = useAllowedNavigation()

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if ((e.key === "k" && (e.metaKey || e.ctrlKey)) || e.key === "/") {
        if (
          (e.target instanceof HTMLElement && e.target.isContentEditable) ||
          e.target instanceof HTMLInputElement ||
          e.target instanceof HTMLTextAreaElement ||
          e.target instanceof HTMLSelectElement
        ) {
          return
        }

        e.preventDefault()
        setOpen((open) => !open)
      }
    }

    document.addEventListener("keydown", down)
    return () => document.removeEventListener("keydown", down)
  }, [])

  const runCommand = React.useCallback((command: () => unknown) => {
    setOpen(false)
    command()
  }, [])

  return (
    <>
      <Button
        variant="outline"
        className={cn(
          "relative justify-start rounded-md bg-background lg:pl-8 text-sm font-normal text-muted-foreground lg:pr-12 p-3 w-full md:w-[200px] lg:w-[336px]",
          shouldShowBuildingSwitcher && "w-10 md:w-10 lg:w-[336px]"
        )}
        onClick={() => setOpen(true)}
        {...props}
      >
        <Search
          className={cn(
            "lg:absolute lg:left-2.5 lg:top-[2.5] size-4 text-muted-foreground"
          )}
        />
        <span
          className={cn(
            "lg:inline-flex ml-2 text-sm font-normal",
            shouldShowBuildingSwitcher && "hidden"
          )}
        >
          Search menu...
        </span>
        <kbd
          className={cn(
            "lg:flex absolute right-2.5 top-2.5 h-5 items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium",
            shouldShowBuildingSwitcher && "hidden"
          )}
        >
          <span className="text-xs">⌘</span>K
        </kbd>
      </Button>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Type a command or search..." />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          {navigation.map((group) => {
            return (
              <CommandGroup key={group.title} heading={group.title}>
                {group.items.map((navItem) => (
                  <CommandItem
                    key={navItem.href}
                    value={navItem.title}
                    onSelect={() => {
                      runCommand(() => navigate(navItem.href as string))
                    }}
                  >
                    <div className="mr-2 flex size-4 items-center justify-center">
                      <CircleIcon className="size-3" />
                    </div>
                    {navItem.title}
                  </CommandItem>
                ))}
              </CommandGroup>
            )
          })}
          <CommandSeparator />
          <CommandGroup heading="Theme">
            <CommandItem onSelect={() => runCommand(() => setTheme("light"))}>
              <SunIcon className="mr-2 size-4" />
              Light
            </CommandItem>
            <CommandItem onSelect={() => runCommand(() => setTheme("dark"))}>
              <MoonIcon className="mr-2 size-4" />
              Dark
            </CommandItem>
            <CommandItem onSelect={() => runCommand(() => setTheme("system"))}>
              <LaptopIcon className="mr-2 size-4" />
              System
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  )
}
